<template>
  <LoginSingleLayout wide>
    <Spinner :status="loading" />
    <div v-show="!loading" class="recover">
      <h1 class="title-xs bold recover__title">
        {{ $t('pages.recover.title') }}
      </h1>
      <p class="body-m recover__description">
        {{ $t('pages.recover.description') }}
      </p>

      <SegmentedTabs v-model="form.contactType" :options="tabs" :aria-label="$t('pages.recover.tabsGroupLabel')" class="recover__tabs" />

      <Alert :i18nData="asyncError" />

      <b-form class="recover__form" @submit.prevent="onSubmit">
        <!-- contact (phone / email) -->
        <div class="nl-field">
          <label class="nl-field__label" for="recover-contact">{{ currentLabel }}</label>
          <div v-if="isPhone" class="nl-field__phone">
            <button
              ref="prefixButton"
              type="button"
              class="nl-prefix"
              aria-haspopup="listbox"
              :aria-expanded="pickerOpen ? 'true' : 'false'"
              aria-controls="nl-prefix-panel"
              :aria-label="$t('pages.newLogin.prefixPicker.triggerLabel', {
                country: currentCountryPrefix.name,
                prefix: currentCountryPrefix.prefix
              })"
              @click="openPrefixPicker"
            >
              <b-img
                :src="`/img/flags/${currentCountryPrefix.code}@1x.png`"
                width="28"
                height="20"
                alt=""
              />
              <span class="nl-prefix__code">+{{ currentCountryPrefix.prefix }}</span>
            </button>
            <input
              id="recover-contact"
              v-model.trim="form.phone"
              type="tel"
              name="tel"
              class="nl-input nl-input--joined"
              required
            >
            <PrefixPicker
              :open="pickerOpen"
              :current-country-prefix="currentCountryPrefix"
              @select="onSelectPrefix"
              @close="closePicker"
            />
          </div>
          <input
            v-else
            id="recover-contact"
            v-model.trim="form.email"
            type="email"
            class="nl-input"
            required
          >
        </div>

        <!-- actions -->
        <div class="recover__actions">
          <button
            type="submit"
            class="nl-submit nl-submit--brand"
            :disabled="loading"
          >
            {{ $t('pages.recover.buttonConfirm') }}
          </button>
          <router-link to="/login" class="nl-link recover__cancel">
            {{ $t('pages.recover.linkCancel') }}
          </router-link>
        </div>
      </b-form>
    </div>
  </LoginSingleLayout>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  E_VALIDATION,
  TYPE_PHONE,
  TYPE_EMAIL
} from '@/constants'
import LoginSingleLayout from '@/components/LoginSingleLayout'
import SegmentedTabs from '@/elements/SegmentedTabs'
import PrefixPicker from '@/components/PrefixPicker.vue'
import { turnOffZoom } from '@/utils'

export default {
  name: 'Recover',
  components: {
    SegmentedTabs,
    LoginSingleLayout,
    PrefixPicker
  },
  data () {
    return {
      form: {
        contactType: TYPE_PHONE,
        email: '',
        phone: ''
      },
      loading: false,
      asyncError: null,
      pickerOpen: false
    }
  },
  computed: {
    ...mapState(['currentCountryPrefix']),
    isPhone () {
      return this.form.contactType === TYPE_PHONE
    },
    contactValue () {
      if (this.isPhone) {
        return this.currentCountryPrefix.prefix + this.form.phone
      } else {
        return this.form.email
      }
    },
    currentLabel () {
      return this.isPhone
        ? this.$t('pages.newLogin.label.phone')
        : this.$t('pages.newLogin.label.email')
    },
    tabs () {
      const phone = { text: this.$t('pages.newLogin.tabs.mobile'), value: TYPE_PHONE }
      const email = { text: this.$t('pages.newLogin.tabs.email'), value: TYPE_EMAIL }
      return [phone, email]
    }
  },
  watch: {
    form: { // clean async error on new input
      handler () { this.asyncError = null },
      deep: true
    },
    'form.contactType' () { // reset fields when switching tab
      this.form.phone = ''
      this.form.email = ''
    }
  },
  methods: {
    ...mapActions(['recoverPassword']),
    openPrefixPicker () {
      this.pickerOpen = !this.pickerOpen
    },
    onSelectPrefix (country) {
      this.$store.commit('SET_CURRENT_COUNTRY_PREFIX', country)
      this.closePicker('select')
    },
    closePicker (reason) {
      this.pickerOpen = false
      // return focus to the trigger on keyboard/selection close, but not when
      // the user dismissed the picker by clicking elsewhere
      if (reason !== 'outside') {
        this.$nextTick(() => {
          if (this.$refs.prefixButton) this.$refs.prefixButton.focus()
        })
      }
    },
    onSubmit () {
      this.asyncError = null
      this.loading = true
      return this.recoverPassword({
        type: this.form.contactType,
        value: this.contactValue
      }).then(() => {
        this.$router.push({ name: 'recoverPin', query: this.$route.query })
      }).catch(err => {
        if (err.id === E_VALIDATION) {
          if (this.isPhone) {
            this.asyncError = {
              key: 'pages.recover.errorPhoneNotValid',
              values: {
                value: '+' + this.currentCountryPrefix.prefix + this.form.phone
              }
            }
          } else {
            this.asyncError = {
              key: 'pages.recover.errorEmailNotValid',
              values: {
                value: this.form.email
              }
            }
          }
        } else {
          throw err
        }
      }).finally(() => {
        this.loading = false
      })
    }
  },
  created () {
    // Default country prefix, previously seeded by PrefixChoiceLayout.
    if (!this.currentCountryPrefix || !this.currentCountryPrefix.prefix) {
      this.$store.commit('SET_CURRENT_COUNTRY_PREFIX', { code: 'IT', prefix: '39', name: 'Italia' })
    }
  },
  mounted () {
    turnOffZoom()
  }
}
</script>

<style lang="scss" scoped>
.recover {
  width: 100%;

  &__title {
    margin-bottom: $spacer*0.5;
    text-align: center;
  }

  &__description {
    margin-bottom: $spacer*1.5;
    text-align: center;
  }

  &__tabs {
    margin-bottom: $spacer*2;
  }

  &__form {
    width: 100%;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacer;
    margin-top: $spacer*2.5;
  }

  &__cancel {
    text-align: center;
  }
}
</style>
